import { Handle, Position } from "@xyflow/react";
import { Box, Typography, useTheme } from "@mui/material";
import ReusableNodes, { NodeContent, NodeError, NodeHandles, NodeHeader, NodeWarning } from "../../../../../components/ReusableNodes";
import { CustomTooltip } from "../../../../../components/TooltipCustomStyle";
import TooltipStatusTitle from "../../../../../components/TooltipStatusTitle";
import ErrorIcon from '@mui/icons-material/Error';
import isSelectedAndHighlight from "../../../../../utils/isSelectedAndHighlight";
// import DrgIcon from "../../drgicons/DrgIcon";

export default function CustomConditionNode({ data, selected, id, onNodeClick, onNodeDoubleClick }) {

    const theme = useTheme();

    const label = data.general_properties.label;
    const description = data.general_properties.description;

    const handleNodeClick = (event) => {
        event.stopPropagation(); // ป้องกัน event bubbling
        if (onNodeClick) {
            onNodeClick(event, id);
        }
    };

    const handleDoubleClick = (event) => {
        // สำคัญมาก! ป้องกันไม่ให้ Event ทะลุไปที่ Background ของ React Flow
        // ไม่งั้นมันอาจจะไปทริกเกอร์การ Zoom เมาส์ หรือ Deselect โหนด
        // event.stopPropagation();

        if (onNodeDoubleClick) {
            onNodeDoubleClick(event, id);
        }
    };

    const getOperandType = (operand) => {
        if (Array.isArray(operand)) {
            return 'array';
        }

        if (typeof operand === 'string') {
            return 'string';
        }

        if (typeof operand === 'number') {
            return 'number';
        }

        if (typeof operand === 'boolean') {
            return 'boolean';
        }

        return 'other';
    };

    const formatSingleValue = (value) => {
        if (typeof value === 'string') {
            return `'${value.replace(/'/g, "\\'")}'`;
        }

        if (typeof value === 'number' || typeof value === 'boolean') {
            return String(value);
        }

        if (value === null) {
            return 'null';
        }

        if (typeof value === 'undefined') {
            return 'undefined';
        }

        return JSON.stringify(value);
    };

    const formatOperandValue = (operand) => {
        if (Array.isArray(operand)) {
            return `[${operand.map((item) => formatSingleValue(item)).join(',')}]`;
        }

        return formatSingleValue(operand);
    };

    const normalizeDisplayOperand = (operand) => {
        const type = getOperandType(operand);

        const colorByType = {
            string: 'text-rose-600',
            number: 'text-sky-600',
            boolean: 'text-sky-600',
            array: 'text-violet-600',
            other: 'text-slate-600',
        };

        return (
            <span className={`${colorByType[type]} text-sm break-all`}>
                {formatOperandValue(operand)}
            </span>
        );
    };

    const hasConjunction = (value) => value !== null && value !== undefined && value !== '';

    const formatLeftOperand = (operand) => {
        if (typeof operand === 'string' || typeof operand === 'number' || typeof operand === 'boolean') {
            return String(operand);
        }

        if (operand === null) {
            return 'null';
        }

        if (typeof operand === 'undefined') {
            return 'undefined';
        }

        return JSON.stringify(operand);
    };

    const getSubConditions = (condition) => {
        if (Array.isArray(condition?.subConditions)) {
            return condition.subConditions;
        }

        if (typeof condition === 'object' && condition !== null) {
            return [condition];
        }

        return [];
    };

    const nodeContent = () => {
        const conditions = data?.general_properties?.conditions || [];

        return conditions.map((condition, index) => {
            const subConditions = getSubConditions(condition);
            const shouldWrapSubCondition = subConditions.length > 1;

            if (!subConditions.length) {
                return null;
            }

            return (
                <div key={index}>
                    <div className="border border-gray-300 rounded-md flex flex-wrap items-center justify-center gap-x-1 gap-y-1 p-2 text-sm leading-4 max-w-full">
                        {shouldWrapSubCondition && <span className="mr-1 text-gray-500">(</span>}

                        {subConditions.map((subCondition, subIndex) => (
                            <span key={subIndex} className="inline-flex flex-wrap items-center gap-1 max-w-full">
                                <span className="font-medium text-gray-700 break-all">{formatLeftOperand(subCondition?.resourceAttribute?.name)}</span>
                                <span className="text-gray-700">{subCondition.operator}</span>
                                {normalizeDisplayOperand(subCondition?.value_property?.value)}
                                {hasConjunction(subCondition.conjunction) && (
                                    <span className="font-semibold text-gray-500">{subCondition.conjunction}</span>
                                )}
                            </span>
                        ))}

                        {shouldWrapSubCondition && <span className="text-gray-500">)</span>}
                    </div>
                    {subConditions.length > 1 && hasConjunction(condition.conjunction) && (
                        <div className="font-semibold text-gray-500 text-sm flex items-center justify-center m-2">{condition.conjunction}</div>
                    )}
                </div>
            );
        });
    };

    const nodeHandles = ({ headerAnchorY } = {}) => {
        const handleClassName = `drg-handle ${selected ? 'drg-selected-handle' : ''}`;

        const sideHandleStyle = typeof headerAnchorY === 'number'
            ? { top: `${headerAnchorY}px` }
            : undefined;

        return (
            <div>
                <Handle
                    id="top-target"
                    type="target"
                    position={Position.Top}
                    className={handleClassName}
                />
                <Handle
                    id="top-source"
                    type="source"
                    position={Position.Top}
                    className={handleClassName}
                />
                <Handle
                    id="bottom-target"
                    type="target"
                    position={Position.Bottom}
                    className={handleClassName}
                />
                <Handle
                    id="bottom-source"
                    type="source"
                    position={Position.Bottom}
                    className={handleClassName}
                />
                <Handle
                    id="left-target"
                    type="target"
                    position={Position.Left}
                    style={sideHandleStyle}
                    className={handleClassName}
                />
                <Handle
                    id="left-source"
                    type="source"
                    position={Position.Left}
                    style={sideHandleStyle}
                    className={handleClassName}
                />
                <Handle
                    id="right-target"
                    type="target"
                    position={Position.Right}
                    style={sideHandleStyle}
                    className={handleClassName}
                />
                <Handle
                    id="right-source"
                    type="source"
                    position={Position.Right}
                    style={sideHandleStyle}
                    className={handleClassName}
                />
            </div>
        )
    };

    return (
        <ReusableNodes
            selected={selected}
            highlighted={data?.highlighted}
            focusMode={data?.focusMode}
            backgroundColor="#ffffff"
            onClick={handleNodeClick}
            onDoubleClick={handleDoubleClick}
        >
            {data?.warning === true && (
                <NodeWarning>
                    <CustomTooltip title={<TooltipStatusTitle headerTitle="Warning" listDetails={data?.warning_text} />} arrow placement="top" tooltipcolorstyle={theme.palette.warning.main}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                background: '#fff',
                                width: 15,
                                height: 15,
                                marginTop: '5px'
                            }}
                        >
                            <ErrorIcon sx={{
                                color: theme.palette.warning.main,
                                fontSize: 18,
                            }} />
                        </Box>
                    </CustomTooltip>
                </NodeWarning>
            )}
            {(data?.error === true || data?.error_text.length > 0) && (
                <NodeError>
                    <CustomTooltip title={<TooltipStatusTitle headerTitle="Error" listDetails={data?.error_text} />} arrow placement="top" tooltipcolorstyle={theme.palette.error.main}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                background: '#fff',
                                width: 15,
                                height: 15,
                                marginTop: '5px'
                            }}
                        >
                            <ErrorIcon sx={{
                                color: theme.palette.error.main,
                                fontSize: 18,
                            }} />
                        </Box>
                    </CustomTooltip>
                </NodeError>
            )}
            <NodeHeader>
                <div className="drg-icon bg-gray-100">
                    <svg
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        fill="none"
                        preserveAspectRatio="xMidYMid meet"
                        className="block h-6 w-6"
                    >
                        <path stroke="none" d="M17.812511 3.202877L20.482762 5.97199C20.544814 6.036284 20.594065 6.112634 20.627622 6.196678C20.661251 6.280716 20.678536 6.370799 20.678536 6.461772C20.678536 6.552753 20.661251 6.642836 20.627622 6.726881C20.594065 6.810918 20.544814 6.887268 20.482762 6.951562L17.812511 9.720676C17.687251 9.850572 17.517368 9.923552 17.34018 9.923552C17.163065 9.923552 16.993182 9.850572 16.867921 9.720676C16.742661 9.590772 16.672292 9.41459 16.672292 9.230886C16.672292 9.047183 16.742661 8.871001 16.867921 8.741104L18.39911 7.154054L13.611 7.154054L8.938097 11.999964L13.611 16.845932L18.39911 16.845932L16.867921 15.258911C16.742661 15.129022 16.672292 14.952847 16.672292 14.769078C16.672292 14.585382 16.742661 14.409207 16.867921 14.279318C16.993182 14.149429 17.163065 14.076456 17.34018 14.076456C17.517368 14.076456 17.687251 14.149429 17.812511 14.279318L20.482762 17.048432C20.544814 17.112726 20.594065 17.189097 20.627622 17.273135C20.661251 17.357172 20.678536 17.447212 20.678536 17.538192C20.678536 17.629173 20.661251 17.719285 20.627622 17.803323C20.594065 17.88736 20.544814 17.963732 20.482762 18.028025L17.812511 20.797139C17.750459 20.861433 17.676836 20.912492 17.595836 20.947279C17.514764 20.982065 17.427906 21.000001 17.34018 21.000001C17.252454 21.000001 17.165669 20.982065 17.084596 20.947279C17.003524 20.912492 16.929901 20.861433 16.867921 20.797139C16.742661 20.66725 16.672292 20.491003 16.672292 20.307307C16.672292 20.216399 16.689577 20.126286 16.723134 20.042249C16.756691 19.958211 16.80587 19.88184 16.867921 19.817546L18.39911 18.230525L13.334804 18.230525C13.247151 18.230598 13.160293 18.212734 13.07922 18.177948C12.99822 18.143233 12.924597 18.092247 12.862545 18.028025L7.718129 12.692297L3.988962 12.692297C3.811913 12.692297 3.642116 12.619325 3.516924 12.489508C3.391732 12.359691 3.3214 12.183588 3.3214 11.999964C3.3214 11.816413 3.391732 11.64031 3.516924 11.510493C3.642116 11.380676 3.811913 11.307704 3.988962 11.307704L7.718129 11.307704L12.862545 5.97199C12.924597 5.907718 12.99822 5.85676 13.07922 5.822017C13.160293 5.787274 13.247151 5.769425 13.334804 5.769497L18.39911 5.769497L16.867921 4.182448C16.742661 4.052551 16.672292 3.876369 16.672292 3.692665C16.672292 3.508959 16.742661 3.332778 16.867921 3.202877C16.993182 3.072979 17.163065 3.000001 17.34018 3.000001C17.517368 3.000001 17.687251 3.072979 17.812511 3.202877Z" fill="#3B67E2" />
                    </svg>
                </div>
                <div className="drg-label">
                    <Typography variant="body2" className="text-gray-900" sx={{ fontWeight: 700 }}>
                        {label}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: 14 }}>
                        {description}
                    </Typography>
                </div>
            </NodeHeader>
            <NodeContent>
                {nodeContent()}
            </NodeContent>
            <NodeHandles>
                {nodeHandles}
            </NodeHandles>
        </ReusableNodes>
    );
}